import React from 'react';
import dayjs from 'dayjs';
import isBoolean from 'lodash/isBoolean';
import dynamic from 'next/dynamic';

import cypressIds from 'Components/cypress_ids.json';
import { SaleTimeControllerWarningMessage } from 'Components/SaleTimeController/SaleTimeControllerWarningMessage/SaleTimeControllerWarningMessage';
import TimeCountdown from 'Components/shared/TimeCountdown/TimeCountdown';
import { useListType } from 'Stores/ListStore/ListStore';
import { useRefetchSaleTimes } from 'Stores/SaleTimesStore/SaleTimesStore';
import { LIST_TYPES } from 'Utilities/consts';
import { applyCypressData, isProd, resolveServerTime } from 'Utilities/helpers';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import useVehicleListRefreshState from 'Utilities/hooks/useVehicleListRefreshState';
import { useClearVehicleListCache } from 'Utilities/hooks/vehicleList/useVehicleListCache/useClearVehicleListCache';

import { vehicleListRefreshObservable } from '../VehicleList.helpers';

import SaleTimeMessage from './SaleTimeMessage/SaleTimeMessage';

import styles from './SaleTime.module.scss';

const SaleTimeController = dynamic(
	() => import(/* webpackChunkName: "bidding_countdown" */ 'Components/SaleTimeController/SaleTimeController'),
);

const SaleTime: React.FC = () => {
	const {
		isLiveSaleEnabled,
		isSaleActive,
		isSaleLaterToday,
		millisecondsRemaining,
		nextSaleStartsAt,
		todaysSaleEndsAt,
	} = useSaleTimes();

	const isSaleTimeController = useFeatureToggle(FEATURES.saleTimeController);
	const isMirageEnabled = useFeatureToggle(FEATURES.mirageServer);
	const { shouldPromptVehicleListRefresh, shouldVehicleListRefresh } = useVehicleListRefreshState();
	const refetchSaleTimes = useRefetchSaleTimes();
	const { clearAllVehicleListCache } = useClearVehicleListCache();
	const listType = useListType();

	const countdownEnd = todaysSaleEndsAt || nextSaleStartsAt;
	const serverTime = resolveServerTime(countdownEnd, millisecondsRemaining);
	const timeDifference = serverTime ? dayjs().diff(serverTime, 'second') : 0; // Adjust time between server and client
	const isSaleTimeControllerEnabled = isSaleTimeController && !isProd;
	const shouldShowSaleTimeControllerWarningMessage = !isMirageEnabled && isSaleTimeControllerEnabled;
	const shouldShowSaleTimeController = isSaleTimeController && !shouldShowSaleTimeControllerWarningMessage;
	const isOutOfLiveSaleOrInLiveSaleWhenEnabled = !isSaleActive || (isLiveSaleEnabled && isSaleActive);
	const shouldDisplaySaleTime = countdownEnd && isOutOfLiveSaleOrInLiveSaleWhenEnabled;
	const isShortlist = listType === LIST_TYPES.shortlist;

	const refreshVehicleList = (shouldPrompt = false) => {
		vehicleListRefreshObservable.notify({ shouldDisplayRefreshPrompt: shouldPrompt });
	};

	const onCountdownEnd = async () => {
		if (!isShortlist) {
			clearAllVehicleListCache();
		}

		if (!isSaleTimeController) {
			await refetchSaleTimes();
		}

		if (shouldVehicleListRefresh || shouldPromptVehicleListRefresh) {
			refreshVehicleList(shouldPromptVehicleListRefresh);
		}
	};

	return (
		<div className={styles.saleTime} {...applyCypressData(cypressIds.text.saleTimeMessage)}>
			{shouldDisplaySaleTime && (
				<TimeCountdown
					key={millisecondsRemaining}
					countdownTime={countdownEnd}
					timeDifference={timeDifference}
					whenCountdownEnds={onCountdownEnd}
				>
					{({ timeLeft }) => {
						const millisecondsRemainingAdjusted = isBoolean(timeLeft) ? 0 : timeLeft?.millisecondsRemaining;
						return (
							<>
								{shouldShowSaleTimeControllerWarningMessage && <SaleTimeControllerWarningMessage />}
								{shouldShowSaleTimeController ? (
									<SaleTimeController millisecondsRemaining={millisecondsRemainingAdjusted}>
										<SaleTimeMessage
											isSaleActive={isSaleActive}
											isSaleLaterToday={isSaleLaterToday}
											millisecondsRemaining={millisecondsRemainingAdjusted}
											nextSaleStartsAt={nextSaleStartsAt}
										/>
									</SaleTimeController>
								) : (
									<SaleTimeMessage
										isSaleActive={isSaleActive}
										isSaleLaterToday={isSaleLaterToday}
										millisecondsRemaining={millisecondsRemainingAdjusted}
										nextSaleStartsAt={nextSaleStartsAt}
									/>
								)}
							</>
						);
					}}
				</TimeCountdown>
			)}
		</div>
	);
};

export default SaleTime;
